<template>
    <div class="xeCostCard heightMAX" :class="{ 'is-overview-compact': isOverviewCompact }">
        <div class="kbet-custom-reset heightMAX">
            <div class="rounded xe-cost-card-container heightMAX" :style="{ backgroundColor: cardBackgroundColor }">
                <!-- 배경 아이콘 -->
                <app-icon class="bg-icon" :icon="widget.bgIcon" :title="widget.bgIcon" />

                <!-- 타이틀 -->
                <p class="title">
                    <span>{{ $t(widget.title) }}</span>
                </p>

                <!-- 메인 값 -->
                <div class="value-usage">
                    <strong>
                        {{ formattedCurrentValue }}
                        <span>&nbsp;{{ $t("원") }}</span>
                    </strong>
                </div>

                <!-- 전월대비 -->
                <dl class="value-contrast">
                    <dt>{{ $t("전월대비") }}</dt>
                    <dd :class="contrastClass">
                        <strong>{{ formattedChangePercent }} %</strong>
                    </dd>
                </dl>
            </div>
        </div>

        <!-- 설정 모달 -->
        <b-modal ref="modalSetting" size="lg" title="설정 - 비용 카드" body-class="p-t-0 p-b-0" hide-footer>
            <cost-card-setting
                :initSetting="costCardSetting"
                @on-modal-cancel="onModalCancel"
                @on-modal-ok="onModalOK"
            />
        </b-modal>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import costCardSetting from "./setting/xeCostCardSetting.vue";

// 상수 정의
const DEFAULT_BACKGROUND_COLOR = "#474f6c";
const DEFAULT_CARD_CONFIG = {
    colorOpens: false,
    titleColor: "#FFFFFFFF",
    titleFontSize: 14,
    backgroundColor: DEFAULT_BACKGROUND_COLOR,
    style: {
        main: {
            fontSize: "40",
            fontWeight: "600",
            fontColor: "#FFFFFFFF",
            unitSize: "15",
            unitColor: "#FFFFFFFF",
            iconColor: "#FFFFFFFF",
            textAlign: "right",
        },
        bottom: {
            fontSize: "15",
            fontWeight: "400",
            fontColor: "#7d8293",
            unitSize: "15",
            unitColor: "#7d8293",
            textAlign: "right",
        },
        progress: {
            patternYn: "",
            animationYn: "",
            color: "#03adad",
            fontSize: "",
            fontWeight: "",
            height: "",
        },
    },
};

const DEFAULT_DATA = {
    currentMonth: {
        totalFee: 0,
        regYm: null,
        isPredicted: false,
        contractCount: 0,
        predictedCount: 0,
    },
    previousMonth: {
        totalFee: 0,
        regYm: null,
        isPredicted: false,
    },
    comparison: {
        amountChange: 0,
        changePercent: 0,
        hasPreviousData: false,
    },
};

export default {
    name: "xeCostCard",
    extends: xeBaseWidget,
    components: {
        costCardSetting,
    },
    data() {
        const isDark = global.xe.parseBool(localStorage.contentInverse);
        const themeColor = isDark
            ? require("../../../components/vue-chartjs/Sample/ChartOptions/DashboardChartColor.json").dark
            : require("../../../components/vue-chartjs/Sample/ChartOptions/DashboardChartColor.json").light;

        return {
            currentMonth: {
                totalFee: 0,
                regYm: null,
                isPredicted: false,
                contractCount: 0,
                predictedCount: 0,
            },
            previousMonth: {
                totalFee: 0,
                regYm: null,
                isPredicted: false,
            },
            comparison: {
                amountChange: 0,
                changePercent: 0,
                hasPreviousData: false,
            },
            themeColor,
        };
    },
    computed: {
        costCardSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },
        cardBackgroundColor() {
            const customColor = this.widget.card?.backgroundColor;
            if (customColor) return customColor;

            const themeColor = this.getThemeCardColor();
            return themeColor || DEFAULT_BACKGROUND_COLOR;
        },
        formattedCurrentValue() {
            const value = this.currentMonth?.totalFee;
            if (value === null || value === undefined) return "0";
            if (typeof value === "number") {
                return value.toLocaleString();
            }
            return String(value);
        },
        formattedChangePercent() {
            const percent = this.comparison?.changePercent;
            if (percent === null || percent === undefined) return 0;
            if (typeof percent === "number") {
                return percent;
            }
            const parsed = parseFloat(percent);
            return isNaN(parsed) ? 0 : parsed;
        },
        contrastClass() {
            const percent = parseFloat(this.formattedChangePercent);
            if (percent > 0) return "contrast-up";
            if (percent < 0) return "contrast-down";
            return "contrast-same";
        },
    },
    created() {
        this.initializeWidget();
    },
    mounted() {
        this.setParentHeight();
    },
    methods: {
        /**
         * 위젯 초기화
         */
        initializeWidget() {
            this.ensureWidgetProperty("style", "");
            this.ensureWidgetProperty("bgIcon", "");
            this.ensureWidgetProperty("card", DEFAULT_CARD_CONFIG);
            this.ensureWidgetProperty("contractIdx", null);
        },
        /**
         * 위젯 속성이 없으면 기본값 설정
         */
        ensureWidgetProperty(property, defaultValue) {
            if (!this.widget[property]) {
                this.$set(this.widget, property, defaultValue);
            }
        },
        /**
         * 테마 카드 색상 가져오기
         */
        getThemeCardColor() {
            if (!this.themeColor?.card?.itemColors) return null;
            if (this.widget.index === undefined) return null;
            return this.themeColor.card.itemColors[this.widget.index] || null;
        },
        /**
         * 부모 요소 높이 설정
         */
        setParentHeight() {
            const elements = document.getElementsByClassName("xeCostCard");
            Array.from(elements).forEach((element) => {
                if (element.parentNode) {
                    element.parentNode.style.height = "100%";
                }
            });
        },
        /**
         * 위젯 조회 조건 반환 (xeCostCard는 독립 API를 사용하므로 빈 배열 반환)
         */
        getWidgetConds() {
            return [];
        },
        /**
         * 데이터 적용
         */
        applyData(data) {
            if (!data) {
                this.resetData();

                return;
            }

            // currentMonth 데이터 적용
            if (data.currentMonth) {
                this.currentMonth.totalFee = data.currentMonth.totalFee ?? 0;
                this.currentMonth.regYm = data.currentMonth.regYm ?? null;
                this.currentMonth.isPredicted = data.currentMonth.isPredicted ?? false;
                this.currentMonth.contractCount = data.currentMonth.contractCount ?? 0;
                this.currentMonth.predictedCount = data.currentMonth.predictedCount ?? 0;
            } else {
                this.currentMonth.totalFee = 0;
                this.currentMonth.regYm = null;
                this.currentMonth.isPredicted = false;
                this.currentMonth.contractCount = 0;
                this.currentMonth.predictedCount = 0;
            }

            // previousMonth 데이터 적용
            if (data.previousMonth) {
                this.previousMonth.totalFee = data.previousMonth.totalFee ?? 0;
                this.previousMonth.regYm = data.previousMonth.regYm ?? null;
                this.previousMonth.isPredicted = data.previousMonth.isPredicted ?? false;
            } else {
                this.previousMonth.totalFee = 0;
                this.previousMonth.regYm = null;
                this.previousMonth.isPredicted = false;
            }

            // comparison 데이터 적용
            if (data.comparison) {
                this.comparison.amountChange = data.comparison.amountChange ?? 0;
                this.comparison.changePercent = data.comparison.changePercent ?? 0;
                this.comparison.hasPreviousData = data.comparison.hasPreviousData ?? false;
            } else {
                this.comparison.amountChange = 0;
                this.comparison.changePercent = 0;
                this.comparison.hasPreviousData = false;
            }
        },
        /**
         * 데이터 초기화
         */
        resetData() {
            this.currentMonth = { ...DEFAULT_DATA.currentMonth };
            this.previousMonth = { ...DEFAULT_DATA.previousMonth };
            this.comparison = { ...DEFAULT_DATA.comparison };
        },
    },
};
</script>

<style scoped>
.xe-cost-card-container {
    position: relative;
    display: grid;
    grid-template-rows: 47px auto 43px;
    -ms-grid-rows: 47px auto 43px;
    min-height: 150px;
    width: 100%;
    height: 100%;
    color: #fff;
    text-align: right;
    box-sizing: border-box;
}

.xeCostCard.is-overview-compact .xe-cost-card-container {
    grid-template-rows: 28px auto 28px;
    -ms-grid-rows: 28px auto 28px;
    min-height: 0;
}

.xe-cost-card-container .bg-icon {
    box-sizing: border-box;
    position: absolute;
    top: 8px;
    left: 10px;
    font-size: 62px;
    opacity: 0.15;
    z-index: 0;
    pointer-events: none;
}

.xeCostCard.is-overview-compact .xe-cost-card-container .bg-icon {
    font-size: 40px;
    top: 4px;
    left: 6px;
}

.xe-cost-card-container .title {
    margin: 0 5.5% 0 5px;
    height: 47px;
    font-size: 16px;
    line-height: 47px;
    font-weight: 700;
}

.xeCostCard.is-overview-compact .xe-cost-card-container .title {
    height: 28px;
    font-size: 12px;
    line-height: 28px;
}

.xe-cost-card-container .title span,
.xe-cost-card-container .title .title-icon {
    display: inline-block;
}

.xe-cost-card-container .value-usage {
    align-self: center;
    -ms-grid-row-align: center;
    margin: 0 5.5% 0 5px;
}

.xe-cost-card-container .value-usage strong {
    display: inline-block;
    font-size: 25px;
    font-weight: 700;
}

.xeCostCard.is-overview-compact .xe-cost-card-container .value-usage strong {
    font-size: 16px;
    line-height: 1.2;
}

.xe-cost-card-container .value-usage span {
    display: inline-block;
    font-size: 14px;
    font-weight: 700;
    vertical-align: top;
    margin: 11px 0 0 3px;
}

.xeCostCard.is-overview-compact .xe-cost-card-container .value-usage span {
    margin-top: 0;
    margin-left: 3px;
    font-size: 11px;
    vertical-align: baseline;
    line-height: 1.2;
}

.xe-cost-card-container .value-contrast {
    position: relative;
    right: 0;
    bottom: 0;
    box-sizing: border-box;
    height: 43px;
    line-height: 40px;
    font-size: 11px;
    border-top: 1px solid rgba(225, 225, 225, 0.2);
    background-color: rgba(0, 0, 0, 0.3);
    margin: 0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    padding: 0 7px;
}

.xe-cost-card-container .value-contrast dt {
    display: inline-block;
    margin-right: 4px;
    line-height: 20px;
}

.xe-cost-card-container .value-contrast dd {
    display: inline-block;
    text-align: right;
    min-width: 60px;
    height: 20px;
    line-height: 20px;
    padding: 0 7px 0 12px;
    margin-right: 3.2%;
    border-radius: 10px;
    background-color: #fff;
}

.xe-cost-card-container .value-contrast dd strong {
    margin-top: -1px;
    display: inline-block;
    line-height: 20px;
    vertical-align: top;
}

.xe-cost-card-container .value-contrast dd.contrast-up,
.xe-cost-card-container .value-contrast dd.contrast-down {
    position: relative;
}

.xe-cost-card-container .value-contrast dd.contrast-up::after,
.xe-cost-card-container .value-contrast dd.contrast-down::after {
    display: inline-block;
    font-size: 10px;
    margin-left: 5px;
}

.xe-cost-card-container .value-contrast dd.contrast-up {
    color: #f55667;
}

.xe-cost-card-container .value-contrast dd.contrast-up::after {
    content: "▲";
}

.xe-cost-card-container .value-contrast dd.contrast-down {
    color: #356aca;
}

.xe-cost-card-container .value-contrast dd.contrast-down::after {
    content: "▼";
}

.xe-cost-card-container .value-contrast dd.contrast-same {
    text-align: center;
    padding: 0 11px 10px 12px;
    color: #888;
}
</style>
